import { ArrowLeft, ArrowRight, BadgeCheck, CalendarCheck, CalendarPlus, Check, ChevronRight, CircleDollarSign, Clock3, Download, ExternalLink, FileText, History, MessageCircle, Pencil, Plus, RefreshCw, Search, Save, Send, Share2, Trash2, TriangleAlert, X, XCircle } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError, apiBlob, newIdempotencyKey } from "../api";
import { useAuth } from "../auth";
import { InfoCard, PageBackLink, PageHeader, ResponsiveDetailSurface, ResponsiveFilters, StateView, StatusBadge } from "../components";
import { CustomerPicker } from "../CustomerPicker";
import { brl, dateTime, invalidateApiData, useApiData, useMediaQuery } from "../hooks";
import { serviceLocationAddress } from "../location-label";
import { brazilianPhoneToE164, formatBrazilianPhone } from "../input-masks";
import { buildWhatsAppUrl, downloadBlob, sharePdf, sharePdfFileOnly, supportsFileShare, supportsWindowsAssistedShare } from "../quote-sharing";
import type { CursorPage, Customer, QuoteCustomerDataSyncResult, QuoteCustomerDataSyncState, QuoteDeliveryPreview, QuoteDetail, QuotePage, ServiceLocation, ServiceType } from "../types";

const exactMoney = (value: string) => {
  const match = value.replace(",", ".").match(/^(\d{1,12})(?:\.(\d{1,2}))?$/);
  return match ? `${match[1]}.${(match[2] ?? "").padEnd(2, "0")}` : null;
};

type QuotePeriod = "today" | "7days" | "30days" | "month" | "year" | "custom";
const quotePeriodLabels: Record<QuotePeriod, string> = {
  today: "Hoje", "7days": "Últimos 7 dias", "30days": "Últimos 30 dias", month: "Este mês", year: "Este ano", custom: "Período personalizado",
};

const dateInput = (date: Date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

const quoteRange = (period: Exclude<QuotePeriod, "custom">) => {
  const today = new Date();
  const from = new Date(today);
  if (period === "7days") from.setDate(today.getDate() - 6);
  if (period === "30days") from.setDate(today.getDate() - 29);
  if (period === "month") from.setDate(1);
  if (period === "year") { from.setMonth(0); from.setDate(1); }
  return { from: dateInput(from), to: dateInput(today) };
};

const appointmentDay = new Intl.DateTimeFormat("pt-BR", {
  weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Sao_Paulo",
});
const appointmentTime = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo",
});
const completionDate = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Sao_Paulo",
});
const appointmentPeriod = (startsAt: string, endsAt: string) => {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const startDay = appointmentDay.format(start);
  const endDay = appointmentDay.format(end);
  return startDay === endDay
    ? `${startDay} · ${appointmentTime.format(start)}–${appointmentTime.format(end)}`
    : `${startDay}, ${appointmentTime.format(start)} → ${endDay}, ${appointmentTime.format(end)}`;
};
const quantityLabel = (quantity: string, unit: string) => `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 3 }).format(Number(quantity))} ${unit}`;
const executionDaysLabel = (days?: number | null) => {
  const value = typeof days === "number" && days >= 1 ? days : 1;
  return `${value} ${value === 1 ? "dia" : "dias"} de execução`;
};
const normalizeExecutionDays = (value: string) => (/^\d{1,3}$/.test(value.trim()) ? Number(value.trim()) : Number.NaN);
const executionDaysValid = (days: number) => Number.isInteger(days) && days >= 1 && days <= 365;

function QuoteForm({ onSaved, initialCustomerId = "" }: { onSaved: () => void; initialCustomerId?: string }) {
  const { user } = useAuth();
  const [customerSearch, setCustomerSearch] = useState("");
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState("");
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedCustomerSearch(customerSearch.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [customerSearch]);
  const customerQuery = useMemo(() => {
    const params = new URLSearchParams({ pageSize: "30", status: "active" });
    if (debouncedCustomerSearch) params.set("q", debouncedCustomerSearch);
    return `/customers?${params}`;
  }, [debouncedCustomerSearch]);
  const customers = useApiData<CursorPage<Customer>>(customerQuery);
  const initialCustomer = useApiData<Customer>(initialCustomerId ? `/customers/${initialCustomerId}` : null);
  const services = useApiData<CursorPage<ServiceType>>("/service-types?pageSize=100&active=true");
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const locations = useApiData<ServiceLocation[]>(customerId ? `/customers/${customerId}/locations` : null);
  const [locationId, setLocationId] = useState("");
  const [items, setItems] = useState<RevisionItem[]>([
    { serviceTypeId: "", quantity: "1", unitPrice: "", discount: "0,00", executionDays: "1" },
  ]);
  const [validUntil, setValidUntil] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const customerOptions = useMemo(() => {
    const options = customers.data?.data ?? [];
    if (!initialCustomer.data || options.some(({ id }) => id === initialCustomer.data!.id)) return options;
    return [initialCustomer.data, ...options];
  }, [customers.data, initialCustomer.data]);
  const selectedCustomer = customerOptions.find(({ id }) => id === customerId) ?? null;

  const updateItem = (index: number, values: Partial<RevisionItem>) => {
    setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...values } : item));
  };
  const chooseService = (index: number, serviceTypeId: string) => {
    const service = services.data?.data.find((item) => item.id === serviceTypeId);
    updateItem(index, { serviceTypeId, unitPrice: service?.defaultPrice.replace(".", ",") ?? "" });
  };
  const addItem = () => {
    setItems((current) => [...current, { serviceTypeId: "", quantity: "1", unitPrice: "", discount: "0,00", executionDays: "1" }]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user?.branchId) return setError("Filial não identificada.");
    if (!customerId) return setError("Selecione um cliente na busca.");
    const normalizedItems = items.map((item) => ({
      serviceTypeId: item.serviceTypeId,
      quantity: item.quantity.replace(",", "."),
      unitPrice: exactMoney(item.unitPrice),
      discount: "0.00",
      executionDays: normalizeExecutionDays(item.executionDays),
    }));
    if (normalizedItems.some((item) => !item.serviceTypeId || !/^\d{1,11}(?:\.\d{1,3})?$/.test(item.quantity) || Number(item.quantity) <= 0 || !item.unitPrice)) {
      return setError("Confira os serviços, quantidades e valores.");
    }
    if (normalizedItems.some((item) => !executionDaysValid(item.executionDays))) {
      return setError("Dias de execução deve ser um número inteiro entre 1 e 365.");
    }
    setBusy(true);
    setError("");
    try {
      await api("/quotes", {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({
          branchId: user.branchId,
          customerId,
          locationId,
          validUntil: validUntil || undefined,
          source: "internal",
          items: normalizedItems,
          discount: "0.00",
          surcharge: "0.00",
          paymentTerms: { method: paymentMethod, installments: 1 },
        }),
      });
      onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Orçamento não salvo."); }
    finally { setBusy(false); }
  };

  return <StateView loading={services.loading} error={services.error}>
    <form className="commercial-form quote-form" onSubmit={submit}>
      <CustomerPicker
        customers={customerOptions}
        selectedCustomer={selectedCustomer}
        value={customerId}
        loading={customers.loading || initialCustomer.loading}
        error={customers.error || initialCustomer.error}
        onChange={(nextCustomerId) => { setCustomerId(nextCustomerId); setLocationId(""); }}
        onSearchChange={setCustomerSearch}
        createHref={`/clientes/novo?returnTo=${encodeURIComponent("/orcamentos/novo")}`}
      />
      <label className="commercial-wide">Endereço do serviço<select value={locationId} onChange={(event) => setLocationId(event.target.value)} required disabled={!customerId}><option value="">Selecione</option>{locations.data?.map((location) => <option key={location.id} value={location.id}>{serviceLocationAddress(location)}</option>)}</select></label>
      <section className="quote-create-services">
        <div className="quote-edit-section-title"><div><strong>Serviços</strong></div><button type="button" onClick={addItem}><Plus size={15} /> Adicionar serviço</button></div>
        <div className="quote-edit-items">{items.map((item, index) => <article key={index}>
          <div className="quote-edit-item-head">
            <label>Serviço<select value={item.serviceTypeId} onChange={(event) => chooseService(index, event.target.value)} required><option value="">Selecione</option>{services.data?.data.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}</select></label>
            {items.length > 1 && <button type="button" className="quote-remove-item" onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remover serviço ${index + 1}`}><Trash2 size={16} /></button>}
          </div>
          <div className="quote-create-item-values">
            <label>Quantidade<input inputMode="decimal" value={item.quantity} onChange={(event) => updateItem(index, { quantity: event.target.value })} required /></label>
            <label>Valor unitário<input inputMode="decimal" value={item.unitPrice} onChange={(event) => updateItem(index, { unitPrice: event.target.value })} required /></label>
<label className="quote-execution-days">Dias de execução<input type="number" inputMode="numeric" min={1} max={365} step={1} value={item.executionDays} onChange={(event) => updateItem(index, { executionDays: event.target.value })} required /></label>
          </div>
        </article>)}</div>
      </section>
      <label>Validade do orçamento<input type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} /></label>
      <label>Pagamento<select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)}><option value="pix">Pix</option><option value="boleto">Boleto</option><option value="credit_card">Cartão</option><option value="bank_transfer">Transferência</option></select></label>
      {error && <div className="form-error commercial-wide">{error}</div>}
      <div className="commercial-form-actions"><Link className="site-button site-button-outline" to="/orcamentos">Cancelar</Link><button className="site-button site-button-navy" disabled={busy} aria-busy={busy}><Save size={16} /> {busy ? "Salvando…" : "Salvar orçamento"}</button></div>
    </form>
  </StateView>;
}

type RevisionItem = { serviceTypeId: string; quantity: string; unitPrice: string; discount: string; executionDays: string };

function QuoteRevisionForm({ quote, onCancel, onSaved }: { quote: QuoteDetail; onCancel: () => void; onSaved: () => void | Promise<void> }) {
  const services = useApiData<CursorPage<ServiceType>>("/service-types?pageSize=100&active=true");
  const [items, setItems] = useState<RevisionItem[]>(() => quote.items.map((item) => ({
    serviceTypeId: item.serviceTypeId, quantity: item.quantity.replace(".", ","), unitPrice: item.unitPrice.replace(".", ","), discount: item.discount.replace(".", ","),
    executionDays: String(typeof item.executionDays === "number" && item.executionDays >= 1 ? item.executionDays : 1),
  })));
  const [validUntil, setValidUntil] = useState(quote.validUntil ?? "");
  const [paymentMethod, setPaymentMethod] = useState(typeof quote.paymentTerms.method === "string" ? quote.paymentTerms.method : "pix");
  const [discount, setDiscount] = useState(quote.discount.replace(".", ","));
  const [surcharge, setSurcharge] = useState(quote.surcharge.replace(".", ","));
  const [notes, setNotes] = useState(quote.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const updateItem = (index: number, values: Partial<RevisionItem>) => setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...values } : item));
  const chooseService = (index: number, serviceTypeId: string) => {
    const service = services.data?.data.find(({ id }) => id === serviceTypeId);
    updateItem(index, { serviceTypeId, unitPrice: service?.defaultPrice.replace(".", ",") ?? "" });
  };
  const addItem = () => {
    const service = services.data?.data[0];
    if (!service) return;
    setItems((current) => [...current, { serviceTypeId: service.id, quantity: "1", unitPrice: service.defaultPrice.replace(".", ","), discount: "0,00", executionDays: "1" }]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (items.length === 0) return setError("Adicione ao menos um serviço.");
    const normalizedItems = items.map((item) => ({
      serviceTypeId: item.serviceTypeId,
      quantity: item.quantity.replace(",", "."),
      unitPrice: exactMoney(item.unitPrice),
      discount: exactMoney(item.discount),
      executionDays: normalizeExecutionDays(item.executionDays),
    }));
    if (normalizedItems.some((item) => !item.serviceTypeId || !/^\d{1,11}(?:\.\d{1,3})?$/.test(item.quantity) || !item.unitPrice || !item.discount)) return setError("Confira os serviços, quantidades e valores.");
    if (normalizedItems.some((item) => !executionDaysValid(item.executionDays))) return setError("Dias de execução deve ser um número inteiro entre 1 e 365.");
    const normalizedDiscount = exactMoney(discount);
    const normalizedSurcharge = exactMoney(surcharge);
    if (!normalizedDiscount || !normalizedSurcharge) return setError("Confira desconto e acréscimo.");
    setBusy(true);
    setError("");
    try {
      const installments = typeof quote.paymentTerms.installments === "number" ? quote.paymentTerms.installments : 1;
      const dueDays = typeof quote.paymentTerms.dueDays === "number" ? quote.paymentTerms.dueDays : undefined;
      await api(`/quotes/${quote.id}/revisions`, {
        method: "POST",
        headers: { "If-Match": `"${quote.version}"`, "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({
          items: normalizedItems,
          discount: normalizedDiscount,
          surcharge: normalizedSurcharge,
          paymentTerms: { method: paymentMethod, installments, ...(dueDays === undefined ? {} : { dueDays }) },
          notes: notes.trim() || undefined,
          validUntil: validUntil || null,
          reason: "Edição do orçamento antes do envio",
        }),
      });
      await onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível salvar as alterações."); }
    finally { setBusy(false); }
  };

  return <StateView loading={services.loading} error={services.error}><form className="workspace-card quote-edit-panel" onSubmit={submit}>
    <header><div><span>{quote.number}</span><h2>Editar orçamento</h2></div><StatusBadge value={quote.status} /></header>
    <div className="quote-edit-context"><span>Cliente<strong>{quote.customerName}</strong></span><span>Endereço<strong>{quote.locationAddress}</strong></span></div>
    <section className="quote-edit-section"><div className="quote-edit-section-title"><div><strong>Serviços</strong><span>Valores e quantidades desta proposta</span></div><button type="button" onClick={addItem}><Plus size={15} /> Adicionar</button></div>
      <div className="quote-edit-items">{items.map((item, index) => <article key={`${item.serviceTypeId}-${index}`}>
        <div className="quote-edit-item-head"><label>Serviço<select value={item.serviceTypeId} onChange={(event) => chooseService(index, event.target.value)} required>{services.data?.data.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}</select></label>{items.length > 1 && <button type="button" className="quote-remove-item" onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remover serviço ${index + 1}`}><Trash2 size={16} /></button>}</div>
        <div className="quote-edit-item-values"><label>Quantidade<input inputMode="decimal" value={item.quantity} onChange={(event) => updateItem(index, { quantity: event.target.value })} required /></label><label>Valor unitário<input inputMode="decimal" value={item.unitPrice} onChange={(event) => updateItem(index, { unitPrice: event.target.value })} required /></label><label>Desconto<input inputMode="decimal" value={item.discount} onChange={(event) => updateItem(index, { discount: event.target.value })} required /></label><label className="quote-execution-days">Dias de execução<input type="number" inputMode="numeric" min={1} max={365} step={1} value={item.executionDays} onChange={(event) => updateItem(index, { executionDays: event.target.value })} required /></label></div>
      </article>)}</div>
    </section>
    <section className="quote-edit-fields">
      <label>Validade<input type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} /></label>
      <label>Pagamento<select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)}><option value="pix">Pix</option><option value="cash">Dinheiro</option><option value="boleto">Boleto</option><option value="credit_card">Cartão de crédito</option><option value="debit_card">Cartão de débito</option><option value="bank_transfer">Transferência</option><option value="other">A combinar</option></select></label>
      <label>Desconto geral<input inputMode="decimal" value={discount} onChange={(event) => setDiscount(event.target.value)} /></label>
      <label>Acréscimo<input inputMode="decimal" value={surcharge} onChange={(event) => setSurcharge(event.target.value)} /></label>
      <label className="quote-edit-notes">Observações<textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={4000} /></label>
    </section>
    {error && <div className="form-error">{error}</div>}
    <div className="quote-edit-actions"><button type="button" className="site-button site-button-outline" onClick={onCancel} disabled={busy}>Cancelar</button><button className="site-button site-button-navy" disabled={busy} aria-busy={busy}><Save size={16} /> {busy ? "Salvando…" : "Salvar alterações"}</button></div>
  </form></StateView>;
}

type QuoteDetailMode = "details" | "edit" | "delivery";

const syncBlockedMessages: Record<Exclude<QuoteCustomerDataSyncState["blockedReason"], null>, string> = {
  fiscal_document_active: "Existe documento fiscal ativo vinculado a este orçamento. Resolva o fluxo fiscal antes de corrigir os dados cadastrais.",
  accepted_requires_confirmation: "Orçamento aceito: a correção cadastral exige confirmação explícita e não altera o aceite, a OS ou o financeiro.",
  cancelled: "Orçamento cancelado não aceita correção cadastral.",
  expired: "Orçamento vencido não aceita correção cadastral.",
};

const syncApiErrorMessages: Record<string, string> = {
  ACCEPTED_CORRECTION_REQUIRED: "Confirme a correção documental do orçamento aceito para continuar.",
  FISCAL_DOCUMENT_ACTIVE: "Existe documento fiscal ativo vinculado a este orçamento. Resolva o fluxo fiscal antes de corrigir os dados cadastrais.",
  QUOTE_TERMINAL_STATE: "Orçamento cancelado ou vencido não aceita correção cadastral.",
  CROSS_ORG_LOCATION: "O endereço selecionado não pertence a este cliente.",
  NO_CUSTOMER_DATA_CHANGES: "Os dados do orçamento já estão sincronizados com o cadastro.",
  INVALID_LOCATION: "Local de serviço não encontrado.",
};

const syncErrorMessage = (caught: unknown, fallback: string) => {
  if (caught instanceof ApiError) {
    const code = typeof caught.body === "object" && caught.body !== null && "code" in caught.body && typeof caught.body.code === "string"
      ? caught.body.code : "";
    if (code === "VERSION_MISMATCH" || caught.status === 409) return "O orçamento foi alterado por outra pessoa. Recarregue os detalhes e tente novamente.";
    if (code && syncApiErrorMessages[code]) return syncApiErrorMessages[code];
  }
  return caught instanceof Error ? caught.message : fallback;
};


function QuoteCustomerDataSync({ quote, busy, onSynced }: {
  quote: QuoteDetail;
  busy: boolean;
  onSynced: (result: QuoteCustomerDataSyncResult) => void | Promise<void>;
}) {
  const sync = quote.customerDataSync;
  const [open, setOpen] = useState(false);
  const [locationId, setLocationId] = useState("");
  const [reason, setReason] = useState("");
  const [confirmAccepted, setConfirmAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const locations = useApiData<ServiceLocation[]>(open ? `/customers/${quote.customerId}/locations` : null);

  useEffect(() => { setOpen(false); setLocationId(""); setReason(""); setConfirmAccepted(false); setError(""); }, [quote.id, quote.version]);

  if (!sync?.hasFrozenSnapshot) return null;

  const blocked = sync.blockedReason === "fiscal_document_active" || sync.blockedReason === "cancelled" || sync.blockedReason === "expired";
  const changingLocation = Boolean(locationId) && locationId !== quote.locationId;
  const nothingToApply = !sync.stale && !changingLocation;
  const reasonInvalid = reason.trim().length < 3 || reason.trim().length > 500;
  const acceptedNeedsConfirmation = sync.blockedReason === "accepted_requires_confirmation";
  const disabled = submitting || busy || blocked || nothingToApply || reasonInvalid || (acceptedNeedsConfirmation && !confirmAccepted);

  const closeForm = () => { setOpen(false); setLocationId(""); setReason(""); setConfirmAccepted(false); setError(""); };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await api<QuoteCustomerDataSyncResult>(`/quotes/${quote.id}/customer-data-sync`, {
        method: "POST",
        headers: { "If-Match": `"${quote.version}"`, "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({
          ...(changingLocation ? { locationId } : {}),
          reason: reason.trim(),
          ...(acceptedNeedsConfirmation ? { confirmAcceptedCorrection: true } : {}),
        }),
      });
      closeForm();
      await onSynced(result);
    } catch (caught) {
      setError(syncErrorMessage(caught, "Não foi possível sincronizar os dados do cliente."));
    } finally { setSubmitting(false); }
  };

  return <section className="quote-sync" aria-label="Dados cadastrais do orçamento">
    {sync.stale && <div className="quote-sync-alert" role="status">
      <TriangleAlert size={16} aria-hidden />
      <div className="quote-sync-alert-body">
        <strong>O PDF congelado está desatualizado</strong>
        <span>Os dados cadastrais do cliente mudaram desde a emissão desta versão.</span>
        <ul className="quote-sync-changes">
          {sync.changes.map((change) => <li key={change.field}>
            <span className="quote-sync-change-label">{change.label}</span>
            <span className="quote-sync-change-values"><span>{change.from || "—"}</span><ArrowRight size={13} aria-hidden /><span>{change.to || "—"}</span></span>
          </li>)}
        </ul>
        {sync.blockedReason && <span className="quote-sync-blocked">{syncBlockedMessages[sync.blockedReason]}</span>}
      </div>
    </div>}
    {!open && <button type="button" className="site-button site-button-outline quote-sync-open" onClick={() => setOpen(true)} disabled={busy}>
      <RefreshCw size={15} /> {sync.stale ? "Revisar e sincronizar dados" : "Trocar endereço do PDF"}
    </button>}
    {open && <form className="quote-sync-form compact-decision" onSubmit={submit}>
      {changingLocation || locations.data ? <label>Endereço do serviço
        <select value={locationId} onChange={(event) => setLocationId(event.target.value)} disabled={submitting || locations.loading || blocked}>
          <option value="">Manter: {sync.current.address}</option>
          {(locations.data ?? []).filter((location) => location.id !== quote.locationId && location.active).map((location) => (
            <option key={location.id} value={location.id}>{serviceLocationAddress(location)}</option>
          ))}
        </select>
      </label> : null}
      {locations.loading && <span className="quote-sync-loading" role="status">Carregando endereços…</span>}
      {locations.error && <span className="quote-sync-loading" role="alert">Não foi possível carregar os endereços do cliente.</span>}
      <label>Motivo da correção (auditoria)
        <textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={500} required placeholder="Ex.: cliente informou novo endereço de entrega" disabled={submitting || blocked} />
      </label>
      {acceptedNeedsConfirmation && <label className="quote-sync-confirm">
        <input type="checkbox" checked={confirmAccepted} onChange={(event) => setConfirmAccepted(event.target.checked)} disabled={submitting} />
        <span>Confirmo a correção documental deste orçamento aceito. O aceite, a OS e o financeiro não serão alterados.</span>
      </label>}
      {blocked && <span className="quote-sync-blocked">{syncBlockedMessages[sync.blockedReason!]}</span>}
      {nothingToApply && !blocked && <span className="quote-sync-blocked">Os dados já estão sincronizados; selecione outro endereço para gerar uma nova versão.</span>}
      {error && <div className="form-error" role="alert">{error}</div>}
      <div>
        <button type="button" className="site-button site-button-outline" onClick={closeForm} disabled={submitting}>Voltar</button>
        <button type="submit" className="site-button site-button-navy" disabled={disabled} aria-busy={submitting}>
          <RefreshCw size={15} /> {submitting ? "Sincronizando…" : "Gerar nova versão"}
        </button>
      </div>
    </form>}
  </section>;
}

function QuoteDocumentHistory({ quote }: { quote: QuoteDetail }) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const download = async (document: QuoteDetail["documents"][number]) => {
    setDownloadingId(document.id);
    setError("");
    try {
      const blob = await apiBlob(document.fileUrl);
      downloadBlob(blob, document.fileName);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível baixar o PDF.");
    } finally { setDownloadingId(null); }
  };

  if (quote.documents.length === 0) return null;
  return <section className="quote-doc-history" aria-label="Histórico documental">
    <h3><History size={14} aria-hidden /> Histórico documental</h3>
    <ul>
      {quote.documents.map((document) => (
        <li key={document.id} className={document.isCurrent ? "current" : ""}>
          <div className="quote-doc-history-info">
            <strong>v{document.versionNo} · {document.fileName}</strong>
            <small>{dateTime(document.createdAt)}</small>
          </div>
          <span className={`quote-doc-tag ${document.isCurrent ? "current" : "superseded"}`}>{document.isCurrent ? "Atual" : "Substituído"}</span>
          <button type="button" className="text-action" onClick={() => void download(document)} disabled={downloadingId !== null} aria-busy={downloadingId === document.id}>
            <Download size={14} /> {downloadingId === document.id ? "Baixando…" : "Baixar PDF"}
          </button>
        </li>
      ))}
    </ul>
    {error && <div className="form-error" role="alert">{error}</div>}
  </section>;
}

function QuotePanel({ quoteId, onSaved, onDetailModeChange }: { quoteId: string; onSaved: () => void; onDetailModeChange?: (mode: QuoteDetailMode) => void }) {
  const money = brl;
  const detail = useApiData<QuoteDetail>(`/quotes/${quoteId}`);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [decision, setDecision] = useState<"submit" | "approve" | "reject" | "accept" | "cancel" | null>(null);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [acceptedBy, setAcceptedBy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<QuoteDeliveryPreview | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [fallbackMode, setFallbackMode] = useState(false);
  const [windowsAssistedShare, setWindowsAssistedShare] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [handoffChannel, setHandoffChannel] = useState<"web_share" | "wa_me_manual" | null>(null);
  const [messageCopied, setMessageCopied] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Aguarde…");
  const [deliveryKey, setDeliveryKey] = useState(newIdempotencyKey());
  const [syncNotice, setSyncNotice] = useState("");

  useEffect(() => { setSyncNotice(""); }, [quoteId]);

  useEffect(() => {
    onDetailModeChange?.(preview ? "delivery" : editing ? "edit" : "details");
  }, [preview, editing, onDetailModeChange]);
  useEffect(() => () => onDetailModeChange?.("details"), [onDetailModeChange]);
  useEffect(() => {
    if (!pdfBlob) { setPdfObjectUrl(null); return; }
    const objectUrl = URL.createObjectURL(pdfBlob);
    setPdfObjectUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [pdfBlob]);

  const startBusy = (label: string) => { setBusyLabel(label); setBusy(true); };
  const finishBusy = () => { setBusy(false); setBusyLabel("Aguarde…"); };

  const openDeliveryPreview = async (aggregateVersion: number): Promise<boolean> => {
    startBusy("Gerando PDF…");
    setError("");
    try {
      const response = await api<QuoteDeliveryPreview>(`/quotes/${quoteId}/delivery-preview`, {
        method: "POST",
        headers: { "If-Match": `"${aggregateVersion}"`, "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({}),
      });
      const documentBlob = await apiBlob(response.document.fileUrl);
      setPreview(response);
      setPdfBlob(documentBlob);
      setMessageText(response.messageText);
      setRecipientPhone(formatBrazilianPhone(response.recipientPhone));
      setFallbackMode(!supportsFileShare());
      setWindowsAssistedShare(supportsWindowsAssistedShare());
      setDownloaded(false);
      setHandoffChannel(null);
      setMessageCopied(false);
      setDeliveryKey(newIdempotencyKey());
      return true;
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível preparar o envio."); return false; }
    finally { finishBusy(); }
  };

  const prepareDraftForDelivery = async (aggregateVersion?: number) => {
    const version = aggregateVersion ?? detail.data?.version;
    if (version === undefined) return;
    startBusy("Preparando envio…");
    setError("");
    try {
      const response = await api<{ status: string; version: number }>(`/quotes/${quoteId}/submit`, {
        method: "POST",
        headers: { "If-Match": `"${version}"`, "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({ reason: "Revisado para envio pelo funcionário" }),
      });
      if (response.status === "ready_to_send") {
        const opened = await openDeliveryPreview(response.version);
        if (!opened) await detail.reload({ background: true });
      } else await detail.reload({ background: true });
      onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível preparar o orçamento."); }
    finally { finishBusy(); }
  };

  const shareNative = async () => {
    if (!preview || !pdfBlob) return;
    startBusy("Compartilhando…");
    setError("");
    try {
      const result = await sharePdf({ blob: pdfBlob, fileName: preview.document.fileName, message: messageText, title: `Orçamento ${preview.number}` });
      setMessageCopied(result.messageCopied);
      setHandoffChannel("web_share");
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setFallbackMode(true);
      setError(caught instanceof Error ? caught.message : "Use o download e abra o WhatsApp.");
    } finally { finishBusy(); }
  };

  const shareOnWindows = async () => {
    if (!preview || !pdfBlob) return;
    startBusy("Compartilhando…");
    setError("");
    try {
      // As duas APIs precisam ser chamadas diretamente pelo clique. O WhatsApp
      // para Windows recebe o PDF, enquanto a mensagem fica pronta para colar.
      const clipboardResult = navigator.clipboard.writeText(messageText);
      const shareResult = sharePdfFileOnly({ blob: pdfBlob, fileName: preview.document.fileName, title: `Orçamento ${preview.number}` });
      await Promise.all([clipboardResult, shareResult]);
      setMessageCopied(true);
      setHandoffChannel("web_share");
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(caught instanceof Error ? caught.message : "Use o download e abra o WhatsApp.");
    } finally { finishBusy(); }
  };

  const downloadPdf = async () => {
    if (!preview || !pdfBlob) return;
    startBusy("Baixando PDF…");
    setError("");
    try {
      downloadBlob(pdfBlob, preview.document.fileName);
      setDownloaded(true);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível baixar o PDF."); }
    finally { finishBusy(); }
  };

  const confirmDelivery = async () => {
    if (!preview || !handoffChannel) return;
    const phone = brazilianPhoneToE164(recipientPhone);
    if (!phone) return setError("Confira o número de WhatsApp.");
    startBusy("Registrando envio…");
    setError("");
    try {
      await api(`/quotes/${quoteId}/deliveries`, {
        method: "POST",
        headers: { "If-Match": `"${preview.version}"`, "Idempotency-Key": deliveryKey },
        body: JSON.stringify({ channel: handoffChannel, recipientName: preview.recipientName, recipientPhone: phone, messageText }),
      });
      setPreview(null);
      await detail.reload({ background: true });
      onSaved();
      invalidateApiData("/quotes");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível registrar o envio."); }
    finally { finishBusy(); }
  };

  const confirmAppointment = async (appointment: QuoteDetail["appointments"][number]) => {
    startBusy("Gerando OS…");
    setError("");
    try {
      const response = await api<{ workOrder: { id: string } }>(`/appointments/${appointment.id}/confirm`, {
        method: "POST",
        headers: { "If-Match": `"${appointment.version}"`, "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({}),
      });
      onSaved();
      invalidateApiData("/quotes");
      invalidateApiData("/appointments");
      invalidateApiData("/work-orders");
      invalidateApiData("/dashboard/summary");
      navigate(`/ordens/${response.workOrder.id}`);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível gerar a OS."); }
    finally { finishBusy(); }
  };

  const act = async (event: FormEvent) => {
    event.preventDefault();
    if (!detail.data || !decision) return;
    startBusy("Salvando…");
    setError("");
    const route = decision === "submit" ? "submit" : decision === "accept" ? "accept" : decision === "cancel" ? "status" : "approval";
    const body = decision === "accept" ? { acceptedByName: acceptedBy } : decision === "cancel" ? { status: "cancelled", reason } : decision === "approve" || decision === "reject" ? { decision: decision === "approve" ? "approved" : "rejected", reason } : { reason };
    try {
      const response = await api<{ status: string; version: number }>(`/quotes/${quoteId}/${route}`, { method: "POST", headers: { "If-Match": `"${detail.data.version}"`, "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify(body) });
      setDecision(null);
      setReason("");
      setAcceptedBy("");
      await detail.reload();
      onSaved();
      if (decision === "approve" && response.status === "ready_to_send") await openDeliveryPreview(response.version);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Ação não concluída."); }
    finally { finishBusy(); }
  };

  const customerDataSynced = async (result: QuoteCustomerDataSyncResult) => {
    await detail.reload();
    onSaved();
    invalidateApiData("/quotes");
    if (result.status === "draft") {
      setSyncNotice(`Versão ${result.currentVersion} criada com os dados atuais do cliente. Preparando o novo PDF…`);
      await prepareDraftForDelivery(result.version);
      return;
    }
    if (result.status === "ready_to_send" || result.status === "accepted") {
      const acceptedCorrection = result.status === "accepted";
      const opened = await openDeliveryPreview(result.version);
      if (!opened) {
        setSyncNotice(acceptedCorrection
          ? `Versão ${result.currentVersion} criada; o aceite foi preservado. Gere o novo PDF pelo fluxo de envio.`
          : `Versão ${result.currentVersion} criada com os dados atuais do cliente. Gere o novo PDF pelo fluxo de envio.`);
      }
      return;
    }
    setSyncNotice(`Versão ${result.currentVersion} criada com os dados atuais do cliente. Reenvie o orçamento para entregar o novo PDF.`);
  };

  const appointmentLine = (appointment: QuoteDetail["appointments"][number]) => <div className="quote-service-schedule" key={appointment.id}>
    <div className="quote-appointment-line"><CalendarCheck size={16} aria-hidden /><span>{appointmentPeriod(appointment.startsAt, appointment.endsAt)}</span><div className="quote-appointment-controls"><StatusBadge value={appointment.status} />{appointment.status === "awaiting_confirmation" && <button type="button" onClick={() => void confirmAppointment(appointment)} disabled={busy} aria-busy={busy}>{busy ? busyLabel : "Gerar OS"}</button>}</div></div>
    {appointment.workOrderId && <Link className="quote-work-order-line" to={`/ordens/${appointment.workOrderId}`}>
      <div><strong>{appointment.workOrderNumber ? `OS ${appointment.workOrderNumber}` : "Ordem de serviço"}</strong>{appointment.completedAt && <time dateTime={appointment.completedAt}>Finalizada em {completionDate.format(new Date(appointment.completedAt))}</time>}</div>
      {appointment.workOrderStatus && <StatusBadge value={appointment.workOrderStatus} />}
    </Link>}
  </div>;

  const quote = detail.data;
  if (editing && quote) return <QuoteRevisionForm quote={quote} onCancel={() => setEditing(false)} onSaved={async () => { await detail.reload({ background: true }); setEditing(false); onSaved(); }} />;
  if (preview) {
    const phone = brazilianPhoneToE164(recipientPhone);
    const whatsappUrl = phone ? buildWhatsAppUrl(phone, messageText) : "#";
    const acceptedCorrection = preview.quoteStatus === "accepted";
    return <aside className="workspace-card quote-send-preview">
      <header className="quote-send-header"><button type="button" onClick={() => { setPreview(null); setPdfBlob(null); }} aria-label="Voltar aos detalhes"><ArrowLeft size={17} /> Voltar</button><div><h2>Enviar orçamento</h2></div></header>
      {syncNotice && <div className="quote-sync-notice" role="status"><Check size={15} aria-hidden /><span>{syncNotice}</span></div>}
      {acceptedCorrection && <div className="quote-sync-alert quote-send-notice" role="status">
        <TriangleAlert size={16} aria-hidden />
        <div className="quote-sync-alert-body">
          <strong>Correção documental de orçamento aceito</strong>
          <span>O novo PDF com os dados cadastrais atualizados está pronto. O reenvio não altera o aceite, a OS ou o financeiro.</span>
        </div>
      </div>}
      <div className="quote-send-summary">
        <span>Cliente<strong>{preview.customerName}</strong></span>
        <span>Valor<strong>{money(preview.total)}</strong></span>
        <span>Validade<strong>{preview.validUntil ? new Date(`${preview.validUntil}T12:00:00`).toLocaleDateString("pt-BR") : "—"}</strong></span>
        <span>Versão<strong>{preview.quoteVersion}</strong></span>
      </div>
      <div className="quote-send-fields">
        <label>Destinatário<input value={preview.recipientName} readOnly /></label>
        <label>WhatsApp<input inputMode="tel" value={recipientPhone} onChange={(event) => setRecipientPhone(formatBrazilianPhone(event.target.value))} /></label>
        <label className="quote-send-message">Mensagem<textarea rows={6} value={messageText} onChange={(event) => setMessageText(event.target.value)} maxLength={4096} /></label>
      </div>
      <section className="quote-pdf-preview">
        <header><div><FileText size={18} /><span><strong>{preview.document.fileName}</strong><small>{Math.max(1, Math.round(preview.document.sizeBytes / 1024))} KB · PDF</small></span></div><a href={pdfObjectUrl ?? undefined} target="_blank" rel="noreferrer" aria-disabled={!pdfObjectUrl} onClick={(event) => { if (!pdfObjectUrl) event.preventDefault(); }}><ExternalLink size={15} /> Abrir PDF</a></header>
        {pdfObjectUrl ? <iframe src={pdfObjectUrl} title={`Pré-visualização do orçamento ${preview.number}`} /> : <div className="quote-pdf-loading" role="status">Carregando PDF…</div>}
      </section>
      {!handoffChannel && <div className="quote-share-actions">
        {!fallbackMode ? <button type="button" className="site-button site-button-red" onClick={() => void shareNative()} disabled={busy} aria-busy={busy}><Share2 size={16} /> {busy ? busyLabel : "Compartilhar PDF"}</button> : <>
          {windowsAssistedShare && <button type="button" className="site-button site-button-red quote-windows-share" onClick={() => void shareOnWindows()} disabled={busy} aria-busy={busy}><Share2 size={16} /> {busy ? busyLabel : "Compartilhar PDF"}</button>}
          <a className={`site-button ${windowsAssistedShare ? "site-button-outline" : "site-button-red"}${!phone ? " disabled" : ""}`} href={whatsappUrl} target="_blank" rel="noreferrer" onClick={(event) => { if (!phone) { event.preventDefault(); setError("Confira o número de WhatsApp."); return; } setHandoffChannel("wa_me_manual"); }}><MessageCircle size={16} /> Abrir WhatsApp</a>
          <button type="button" className="site-button site-button-outline quote-download-action" onClick={() => void downloadPdf()} disabled={busy} aria-busy={busy}><Download size={16} /> {busy ? busyLabel : downloaded ? "Baixar novamente" : "Baixar PDF"}</button>
          {windowsAssistedShare && <p className="quote-windows-hint">A mensagem será copiada. Depois de escolher o WhatsApp, pressione <kbd>Ctrl+V</kbd>.</p>}
          <p>{windowsAssistedShare ? "Alternativa: baixe o PDF e abra a conversa com a mensagem preenchida." : "Baixe o PDF e anexe-o na conversa. A mensagem já será preenchida."}</p>
        </>}
      </div>}
      {handoffChannel && <div className="quote-delivery-confirmation" role="status">
        <div><strong>O orçamento foi enviado no WhatsApp?</strong><span>{acceptedCorrection ? "Registrar apenas a entrega da nova versão; o aceite continua o mesmo." : messageCopied ? "A mensagem foi copiada. Cole-a na conversa junto ao PDF." : "O sistema registrará uma confirmação feita por você, não uma confirmação de entrega do WhatsApp."}</span></div>
        <div><button type="button" className="site-button site-button-outline" onClick={() => setHandoffChannel(null)} disabled={busy}>Ainda não</button><button type="button" className="site-button site-button-red" onClick={() => void confirmDelivery()} disabled={busy} aria-busy={busy}><Check size={16} /> {busy ? busyLabel : "Sim, registrar envio"}</button></div>
      </div>}
      {error && <div className="form-error">{error}</div>}
    </aside>;
  }
  return <StateView loading={detail.loading} error={detail.error}>{quote && <aside className="workspace-card commercial-detail quote-detail record-detail">
    <header className="record-detail-heading"><div><span>{quote.number}</span><h2>{quote.customerName}</h2></div><StatusBadge value={quote.status} /></header>
    <div className="record-detail-facts quote-facts"><span>Valor<strong>{money(quote.total)}</strong></span><span>Validade<strong>{quote.validUntil ? new Date(`${quote.validUntil}T12:00:00`).toLocaleDateString("pt-BR") : "—"}</strong></span></div>
    {syncNotice && <div className="quote-sync-notice" role="status"><Check size={15} aria-hidden /><span>{syncNotice}</span></div>}
    <QuoteCustomerDataSync quote={quote} busy={busy} onSynced={customerDataSynced} />
    <section className="quote-service-list" aria-label="Serviços do orçamento">{quote.items.map((item) => {
      const appointments = quote.appointments.filter((appointment) => appointment.serviceName === item.serviceName);
      return <article className="quote-service-card" key={item.id}>
        <div className="quote-service-main"><span><strong>{item.serviceName}</strong><small>{quantityLabel(item.quantity, item.unit)}</small><small className="quote-execution-days-label">{executionDaysLabel(item.executionDays)}</small></span>{quote.items.length > 1 && <strong>{money(item.total)}</strong>}</div>
        {appointments.map(appointmentLine)}
      </article>;
    })}{quote.appointments.filter((appointment) => !quote.items.some((item) => item.serviceName === appointment.serviceName)).map((appointment) => <article className="quote-service-card" key={appointment.id}><div className="quote-service-main"><span><strong>{appointment.serviceName}</strong></span></div>{appointmentLine(appointment)}</article>)}</section>
    {!decision && <div className="quote-actions record-detail-actions">
      {["draft", "ready_to_send"].includes(quote.status) && <button className="site-button site-button-outline" onClick={() => setEditing(true)} disabled={busy}><Pencil size={16} /> Editar</button>}
      {quote.status === "draft" && <button className="site-button site-button-red" onClick={() => void prepareDraftForDelivery()} disabled={busy} aria-busy={busy}><Send size={16} /> {busy ? busyLabel : "Enviar orçamento"}</button>}
      {quote.status === "pending_approval" && user?.permissions.includes("quotes.approve") && <><button className="site-button site-button-navy" onClick={() => setDecision("approve")}><Check size={16} /> Aprovar</button><button className="site-button site-button-outline" onClick={() => setDecision("reject")}>Rejeitar</button></>}
      {quote.status === "ready_to_send" && <button className="site-button site-button-red" onClick={() => void openDeliveryPreview(quote.version)} disabled={busy} aria-busy={busy}><Send size={16} /> {busy ? busyLabel : "Enviar orçamento"}</button>}
      {quote.status === "sent" && <><button className="site-button site-button-outline" onClick={() => void openDeliveryPreview(quote.version)} disabled={busy} aria-busy={busy}><Send size={16} /> {busy ? busyLabel : "Reenviar"}</button><button className="site-button site-button-navy" onClick={() => setDecision("accept")}><Check size={16} /> Registrar aceite</button></>}
      {quote.status === "accepted" && quote.appointments.length === 0 && <Link className="site-button site-button-red" to={`/agenda/novo?quoteId=${quote.id}`}><CalendarPlus size={16} /> Agendar</Link>}
      {!['accepted', 'cancelled', 'expired'].includes(quote.status) && <button className="text-action" onClick={() => setDecision("cancel")}><XCircle size={15} /> Cancelar</button>}
    </div>}
    {decision && <form className="compact-decision" onSubmit={act}>{decision === "accept" ? <label>Nome de quem aceitou<input value={acceptedBy} onChange={(event) => setAcceptedBy(event.target.value)} minLength={2} required /></label> : <label>Motivo<input value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} required /></label>}<div><button type="button" className="site-button site-button-outline" onClick={() => setDecision(null)}>Voltar</button><button className="site-button site-button-navy" disabled={busy} aria-busy={busy}>{busy ? busyLabel : "Confirmar"}</button></div></form>}
    <QuoteDocumentHistory quote={quote} />
    {quote.revisions.length > 1 && <div className="detail-history"><History size={15} /><span>{quote.revisions.length} versões · última em {dateTime(quote.revisions[0]!.createdAt)}</span></div>}
    {error && <div className="form-error">{error}</div>}
  </aside>}</StateView>;
}

export function CommercialPage() {
  const money = brl;
  const initialRange = useMemo(() => quoteRange("month"), []);
  const [period, setPeriod] = useState<QuotePeriod>("month");
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailMode, setDetailMode] = useState<QuoteDetailMode>("details");
  const compactDetail = useMediaQuery("(max-width: 780px)");

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ pageSize: "100", from, to });
    if (status) params.set("status", status);
    if (debouncedSearch) params.set("q", debouncedSearch);
    return `/quotes?${params}`;
  }, [from, to, status, debouncedSearch]);
  const quotes = useApiData<QuotePage>(query);

  useEffect(() => {
    const visible = quotes.data?.data ?? [];
    if (visible.length === 0) { setSelectedId(null); return; }
    if (compactDetail) {
      if (selectedId && !visible.some(({ id }) => id === selectedId)) setSelectedId(null);
      return;
    }
    if (!selectedId || !visible.some(({ id }) => id === selectedId)) setSelectedId(visible[0]!.id);
  }, [quotes.data, selectedId, compactDetail]);

  useEffect(() => {
    if (compactDetail) setSelectedId(null);
  }, [compactDetail]);

  const choosePeriod = (next: Exclude<QuotePeriod, "custom">) => {
    const range = quoteRange(next);
    setPeriod(next);
    setFrom(range.from);
    setTo(range.to);
  };

  const clearFilters = () => {
    const range = quoteRange("month");
    setPeriod("month");
    setFrom(range.from);
    setTo(range.to);
    setStatus("");
    setSearch("");
  };

  const summary = quotes.data?.summary ?? { count: 0, total: "0.00", acceptedCount: 0, acceptedTotal: "0.00", openCount: 0 };
  const activeFilterCount = Number(period !== "month") + Number(Boolean(status)) + Number(Boolean(search.trim()));
  return <>
    <section className="page-intro"><div className="app-container"><PageHeader title="Orçamentos" /></div></section>
    <section className="app-section compact"><div className="app-container quotes-workspace">
      <div className="quote-summary-grid order-summary">
        <InfoCard icon={FileText} label="Orçamentos" value={summary.count} tone="soft" />
        <InfoCard icon={CircleDollarSign} label="Valor total" value={<span className="compact-money">{money(summary.total)}</span>} tone="blue" />
        <div className="quote-summary-accepted"><InfoCard icon={BadgeCheck} label="Aceitos" value={<span className="compact-money">{money(summary.acceptedTotal)}</span>} tone="green" /><span className="quote-summary-count">{summary.acceptedCount}</span></div>
        <InfoCard icon={Clock3} label="Em aberto" value={summary.openCount} tone="amber" />
      </div>

      <ResponsiveFilters className="workspace-card quote-filter-panel" activeCount={activeFilterCount} summary={quotePeriodLabels[period]}>
        <div className="admin-tabs quote-period-tabs">{([['today', 'Hoje', 'Hoje'], ['7days', '7 dias', '7d'], ['30days', '30 dias', '30d'], ['month', 'Este mês', 'Mês'], ['year', 'Este ano', 'Ano']] as const).map(([value, label, mobileLabel]) => <button key={value} className={period === value ? "active" : ""} onClick={() => choosePeriod(value)}><span className="filter-label-desktop">{label}</span><span className="filter-label-mobile">{mobileLabel}</span></button>)}</div>
        <div className="quote-filter-fields">
          <label className="quote-search"><span>Buscar</span><div><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cliente ou número" /></div></label>
          <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">Todos</option><option value="draft">Rascunho</option><option value="pending_approval">Aguardando aprovação</option><option value="ready_to_send">Pronto para envio</option><option value="sent">Enviado</option><option value="accepted">Aceito</option><option value="refused">Recusado</option><option value="expired">Vencido</option><option value="cancelled">Cancelado</option></select></label>
          <label><span>De</span><input type="date" value={from} max={to} onChange={(event) => { setFrom(event.target.value); setPeriod("custom"); }} /></label>
          <label><span>Até</span><input type="date" value={to} min={from} onChange={(event) => { setTo(event.target.value); setPeriod("custom"); }} /></label>
          <button className="quote-clear-filter" onClick={clearFilters}><X size={15} /> Limpar</button>
        </div>
      </ResponsiveFilters>

      <StateView loading={quotes.loading} error={quotes.error}>
        <div className={`commercial-layout${detailMode === "delivery" ? " quote-delivery-layout" : ""}`}>
          <section className="workspace-card commercial-list quote-list"><header className="workspace-card-header"><h2>Resultado</h2><span>{summary.count}</span></header>{quotes.data?.data.map((quote) => <button key={quote.id} className={selectedId === quote.id ? "commercial-row active" : "commercial-row"} onClick={() => setSelectedId(quote.id)}><div><strong>{quote.customerName}</strong><small>{quote.createdAt ? new Date(quote.createdAt).toLocaleDateString("pt-BR") : "—"} · {quote.number} · {quote.locationAddress}</small></div><b>{money(quote.total)}</b><StatusBadge value={quote.status} /><ChevronRight size={17} /></button>)}{quotes.data?.data.length === 0 && <div className="quote-list-empty"><strong>Nenhum orçamento no período</strong></div>}</section>
          {selectedId ? <ResponsiveDetailSurface title={detailMode === "delivery" ? "Enviar orçamento" : detailMode === "edit" ? "Editar orçamento" : "Detalhes do orçamento"} onClose={() => setSelectedId(null)}><QuotePanel quoteId={selectedId} onSaved={() => void quotes.reload({ background: true })} onDetailModeChange={setDetailMode} /></ResponsiveDetailSurface> : !compactDetail && <aside className="commercial-placeholder quote-empty-detail">Selecione um orçamento.</aside>}
        </div>
      </StateView>
    </div></section>
  </>;
}

export function NewQuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCustomerId = searchParams.get("customerId") ?? "";
  return <>
    <section className="page-intro"><div className="app-container"><PageBackLink to="/orcamentos" context="Orçamentos" /><PageHeader title="Novo orçamento" /></div></section>
    <section className="app-section compact"><div className="app-container commercial-stack"><QuoteForm initialCustomerId={initialCustomerId} onSaved={() => navigate("/orcamentos", { replace: true })} /></div></section>
  </>;
}
